import React, { useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Link } from "react-router-dom";
import { useStoryInteractions } from "../../../api/queries/story-interactions/useStoryInteractions";
import { useUpdateStoryInteractionStatus } from "../../../api/queries/story-interactions/useUpdateStoryInteractionStatus";
import {
  StoryInteractionItem,
  StoryInteractionIntent,
  StoryInteractionStatus,
} from "../../../api/types/story-interactions";

const statusOptions = [
  { label: "All statuses", value: "" },
  { label: "New", value: StoryInteractionStatus.NEW },
  { label: "In progress", value: StoryInteractionStatus.IN_PROGRESS },
  { label: "Solved", value: StoryInteractionStatus.SOLVED },
];

const intentOptions = [
  { label: "All intents", value: "" },
  { label: "Connect with", value: StoryInteractionIntent.CONNECT_WITH },
  { label: "Ask question", value: StoryInteractionIntent.ASK_QUESTION },
];

const toFilterValue = (value: unknown): string => {
  if (typeof value === "string") {
    return value;
  }

  if (value && typeof value === "object" && "value" in value) {
    const optionValue = (value as { value?: unknown }).value;
    if (typeof optionValue === "string") {
      return optionValue;
    }
  }

  return "";
};

export default function StoryInteractionsList() {
  const [status, setStatus] = useState<string>("");
  const [intent, setIntent] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [processingId, setProcessingId] = useState<string | null>(null);

  const filters = useMemo(
    () => ({
      status: status || undefined,
      intent: intent || undefined,
      q: search.trim() || undefined,
    }),
    [status, intent, search],
  );

  const { data, isLoading, refetch } = useStoryInteractions(filters);
  const updateStatus = useUpdateStoryInteractionStatus();

  const handleUpdateStatus = async (
    id: string,
    nextStatus: StoryInteractionStatus,
  ) => {
    setProcessingId(id);
    try {
      await updateStatus.mutateAsync({ id, status: nextStatus });
      refetch();
    } finally {
      setProcessingId(null);
    }
  };

  const senderTemplate = (rowData: StoryInteractionItem) => {
    return `${rowData.senderFirstName} ${rowData.senderLastName}`.trim();
  };

  const profileTemplate = (rowData: StoryInteractionItem) => {
    const profileName = rowData.profileName?.trim() || rowData.profileId;
    if (!rowData.profileId) {
      return profileName;
    }

    return (
      <Link
        to={`/people/${rowData.profileId}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {profileName}
      </Link>
    );
  };

  const dateTemplate = (rowData: StoryInteractionItem) => {
    return new Date(rowData.createdAt).toLocaleString();
  };

  const messageTemplate = (rowData: StoryInteractionItem) => {
    if (!rowData.message) {
      return <span className="text-muted">-</span>;
    }

    if (rowData.message.length <= 80) {
      return rowData.message;
    }

    return `${rowData.message.slice(0, 80)}...`;
  };

  const actionTemplate = (rowData: StoryInteractionItem) => {
    const isLoadingRow = processingId === rowData.id;

    if (rowData.status === StoryInteractionStatus.NEW) {
      return (
        <Button
          label="Start"
          size="small"
          icon="pi pi-play"
          loading={isLoadingRow}
          disabled={isLoadingRow}
          onClick={() =>
            handleUpdateStatus(rowData.id, StoryInteractionStatus.IN_PROGRESS)
          }
        />
      );
    }

    if (rowData.status === StoryInteractionStatus.IN_PROGRESS) {
      return (
        <div className="flex gap-2">
          <Button
            label="Back to New"
            size="small"
            severity="secondary"
            icon="pi pi-undo"
            loading={isLoadingRow}
            disabled={isLoadingRow}
            onClick={() =>
              handleUpdateStatus(rowData.id, StoryInteractionStatus.NEW)
            }
          />
          <Button
            label="Mark Solved"
            size="small"
            icon="pi pi-check"
            loading={isLoadingRow}
            disabled={isLoadingRow}
            onClick={() =>
              handleUpdateStatus(rowData.id, StoryInteractionStatus.SOLVED)
            }
          />
        </div>
      );
    }

    return (
      <Button
        label="Reopen"
        size="small"
        severity="secondary"
        icon="pi pi-refresh"
        loading={isLoadingRow}
        disabled={isLoadingRow}
        onClick={() =>
          handleUpdateStatus(rowData.id, StoryInteractionStatus.IN_PROGRESS)
        }
      />
    );
  };

  return (
    <div className="col-12">
      <div className="flex flex-column gap-3 py-3">
        <h1 className="m-0">Story Interactions</h1>

        <div className="flex gap-2 flex-wrap">
          <Dropdown
            value={status}
            options={statusOptions}
            onChange={(e) => setStatus(toFilterValue(e.value))}
            placeholder="Status"
            style={{ minWidth: "180px" }}
          />
          <Dropdown
            value={intent}
            options={intentOptions}
            onChange={(e) => setIntent(toFilterValue(e.value))}
            placeholder="Intent"
            style={{ minWidth: "180px" }}
          />
          <InputText
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search sender/email/message..."
            style={{ minWidth: "280px" }}
          />
        </div>
      </div>

      <DataTable
        value={data?.data || []}
        loading={isLoading}
        paginator
        rows={25}
        emptyMessage="No interactions found"
        className="p-datatable-sm"
        stripedRows
      >
        <Column header="Created" body={dateTemplate} sortable />
        <Column field="status" header="Status" sortable />
        <Column field="intent" header="Intent" sortable />
        <Column field="ctaPlacement" header="Placement" />
        <Column field="storyToken" header="Token" />
        <Column header="Profile" body={profileTemplate} />
        <Column header="Sender" body={senderTemplate} />
        <Column field="senderEmail" header="Email" />
        <Column header="Message" body={messageTemplate} />
        <Column header="Actions" body={actionTemplate} />
      </DataTable>
    </div>
  );
}
